import {
  Box,
  Button,
  Group,
  LoadingOverlay,
  Modal,
  Stack,
  Text
} from '@mantine/core'
import { FormattedMessage, useIntl } from 'react-intl'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import { useRestartAiBridge } from '@/queries/aiBridgeQueries'
import { showToast } from '@/theme/notifications'
import { AiBridge } from '@/types/aiBridge'
import { useAiBridgeOperationLog } from './useAiBridgeOperationLog'
import { useAiBridgeTest } from './useAiBridgeTest'

type RestartAiBridgeModalProps = {
  opened: boolean
  aiBridge?: Pick<AiBridge, 'id' | 'name'>
  onClose: () => void
  onRestart: () => void
}

export const RestartAiBridgeModal = ({
  opened,
  aiBridge,
  onClose,
  onRestart
}: RestartAiBridgeModalProps) => {
  const intl = useIntl()

  const { mutateAsync: restartAiBridge, isPending } = useRestartAiBridge()

  const { testBridge, isTesting } = useAiBridgeTest()

  const { recordOperation } = useAiBridgeOperationLog()

  const handleRestartAiBridge = async () => {
    if (!aiBridge) {
      return
    }

    // Check the compose file is actually on the device first: restart is a one-way
    // RPC, so restarting a bridge whose files are missing would fail silently.
    const check = await testBridge(aiBridge.id)

    if (check.status !== 'passed') {
      const message = `${intl.formatMessage({
        id: 'aiBridge.restart.checkFailed'
      })} ${check.message}`

      recordOperation(aiBridge.id, {
        type: 'restart',
        status: 'failed',
        message
      })

      showToast(message, 'error')

      return
    }

    try {
      await restartAiBridge(aiBridge.id)

      // The RPC is one-way, so this confirms the request reached the device,
      // not that the bridge came back up.
      const message = intl.formatMessage({ id: 'aiBridge.restart.requested' })

      recordOperation(aiBridge.id, {
        type: 'restart',
        status: 'passed',
        message
      })

      showToast(
        intl.formatMessage({ id: 'aiBridge.restart.inProgress' }),
        'info'
      )
      onRestart()
    } catch (err) {
      const message = extractErrorMessage(
        err,
        intl.formatMessage({ id: 'aiBridge.restart.error' })
      )

      recordOperation(aiBridge.id, {
        type: 'restart',
        status: 'failed',
        message
      })

      showToast(message, 'error')
    }
  }

  return (
    <Modal
      size="lg"
      title={<FormattedMessage id="aiBridge.restart" />}
      opened={opened}
      onClose={onClose}
    >
      <Box pos="relative">
        <LoadingOverlay visible={isTesting || isPending} />

        <Stack gap="xl">
          {aiBridge && (
            <Text size="sm">
              <FormattedMessage
                id="aiBridge.restart.confirmation"
                values={{
                  name: <span>{aiBridge.name}</span>
                }}
              />
            </Text>
          )}

          <Group justify="end">
            <Button miw={120} variant="default" onClick={onClose}>
              <FormattedMessage id="cancel" />
            </Button>

            <Button
              miw={120}
              loading={isTesting || isPending}
              onClick={() => void handleRestartAiBridge()}
            >
              <FormattedMessage id="aiBridge.restart" />
            </Button>
          </Group>
        </Stack>
      </Box>
    </Modal>
  )
}
